import React from 'react';
import PropTypes from 'prop-types';
import { Box, Stack, Typography, useMediaQuery } from '@mui/material';
import Iconify from './Iconify';
import SplitMeter from './SplitMeter';
import { TIERS, isTierUnlocked, currentTierNumber, nextTier, nextTierProgress } from '../utils/streak';
import { DUR, EASE_OUT, STAGGER } from '../utils/motion';
import { LABEL_MICRO, LABEL_SM } from '../utils/typeScale';
import { RED, YELLOW, DARK_TEXT } from '../utils/brandColors';

TierLadder.propTypes = {
  streak: PropTypes.object,
};

// Escalation encoded in size: a 30-day tier must not look like a 3-day tier.
const TITLE_SIZE = { 1: 14, 2: 15, 3: 16 };
const TITLE_WEIGHT = { 1: 700, 2: 800, 3: 900 };

/**
 * A single vertical ladder replacing three copy-pasted Paper blocks. The rail
 * between nodes is RED up to the tier the user holds and `divider` above it,
 * so progression is legible at a glance rather than requiring three chips to
 * be read and compared.
 */
export default function TierLadder({ streak }) {
  const prefersReducedMotion = useMediaQuery('(prefers-reduced-motion: reduce)');
  const held = currentTierNumber(streak);
  const next = nextTier(streak);
  const nextProgress = nextTierProgress(next, streak);

  return (
    <Box sx={{ position: 'relative' }}>
      {/* Rail — sits behind the nodes, inset to their centre line. */}
      <Box
        aria-hidden
        sx={{
          position: 'absolute',
          left: 15,
          top: 16,
          bottom: 16,
          width: 2,
          bgcolor: 'divider',
        }}
      />
      {held > 0 ? (
        <Box
          aria-hidden
          sx={{
            position: 'absolute',
            left: 15,
            top: 16,
            width: 2,
            height: `${((held - 0.5) / TIERS.length) * 100}%`,
            bgcolor: RED,
          }}
        />
      ) : null}

      {TIERS.map((tier, index) => {
        const unlocked = isTierUnlocked(tier, streak);
        const isCurrent = tier.n === held;
        const isNext = next && tier.n === next.n;

        return (
          <Box
            key={tier.n}
            sx={{
              position: 'relative',
              display: 'grid',
              gridTemplateColumns: '32px 1fr auto',
              alignItems: 'center',
              gap: 2,
              py: 2,
              animation: prefersReducedMotion
                ? 'none'
                : `rowIn ${DUR.reveal}ms ${EASE_OUT} ${index * STAGGER}ms both`,
              '@keyframes rowIn': {
                from: { opacity: 0, transform: 'translateY(6px)' },
                to: { opacity: 1, transform: 'translateY(0)' },
              },
            }}
          >
            <TierNode tier={tier} unlocked={unlocked} isCurrent={isCurrent} />

            <Box>
              <Typography
                sx={{
                  fontSize: TITLE_SIZE[tier.n],
                  fontWeight: TITLE_WEIGHT[tier.n],
                  letterSpacing: '-0.01em',
                  color: unlocked ? 'text.primary' : 'text.secondary',
                }}
              >
                {tier.title}
              </Typography>
              <Typography sx={{ ...LABEL_SM, mt: 0.25 }}>{tier.reward}</Typography>

              {isNext && nextProgress ? (
                <Box sx={{ mt: 1.25, maxWidth: 220 }}>
                  {/* No `trackColor`: the old black-alpha track was invisible
                      against a dark-mode surface. The default falls back to
                      the theme's `action.disabledBackground` token, which
                      inverts with the palette. */}
                  <SplitMeter
                    percentA={nextProgress.percent}
                    height={4}
                    fillColor={RED}
                    reducedMotion={prefersReducedMotion}
                  />
                  <Typography sx={{ ...LABEL_MICRO, mt: 0.75 }}>
                    {nextProgress.remaining} {nextProgress.unit}
                    {nextProgress.remaining === 1 ? '' : 's'} to tier {tier.n}
                  </Typography>
                </Box>
              ) : null}
            </Box>

            <Box sx={{ textAlign: 'right' }}>
              {unlocked && tier.n === 3 ? (
                <Box
                  sx={{
                    display: 'inline-block',
                    bgcolor: YELLOW,
                    color: DARK_TEXT,
                    borderRadius: '8px',
                    px: 1,
                    py: 0.25,
                    fontSize: 11,
                    fontWeight: 800,
                    letterSpacing: '0.12em',
                  }}
                >
                  MAX
                </Box>
              ) : (
                <Typography sx={LABEL_MICRO}>
                  {unlocked ? 'Unlocked' : `${tier.days}d / ${tier.votes} votes`}
                </Typography>
              )}
            </Box>
          </Box>
        );
      })}
    </Box>
  );
}

TierNode.propTypes = {
  tier: PropTypes.object.isRequired,
  unlocked: PropTypes.bool,
  isCurrent: PropTypes.bool,
};

function TierNode({ tier, unlocked, isCurrent }) {
  const base = {
    position: 'relative',
    width: 32,
    height: 32,
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    bgcolor: 'background.paper',
    flexShrink: 0,
  };

  if (unlocked && !isCurrent) {
    return (
      <Box sx={{ ...base, bgcolor: RED }}>
        <Iconify icon="eva:checkmark-fill" width={16} height={16} sx={{ color: '#fff' }} />
      </Box>
    );
  }
  if (isCurrent) {
    return (
      <Box sx={{ ...base, border: '2px solid', borderColor: RED }}>
        <Typography sx={{ fontSize: 13, fontWeight: 900, color: RED }}>{tier.n}</Typography>
      </Box>
    );
  }
  return (
    <Box sx={{ ...base, border: '1px solid', borderColor: 'divider' }}>
      <Typography sx={{ fontSize: 13, fontWeight: 800, color: 'text.disabled' }}>{tier.n}</Typography>
    </Box>
  );
}
